import { Attribute } from '@tiptap/core';

/**
 * Ensures all attribute objects have an explicit 'default: undefined' property.
 *
 * This is necessary for correct attribute comparison between documents during diff operations.
 * Without explicit undefined defaults, object equality checks may produce incorrect results
 * when comparing attributes that implicitly vs explicitly have undefined defaults.
 *
 * @param attributes - Record of attribute key-value pairs to process
 * @returns A new record with all attributes having explicit undefined defaults
 */
export const withUndefinedDefaults = (
  attributes: Record<string, Attribute>
) => {
  return Object.entries(attributes).reduce<Record<string, Attribute>>(
    (result, [key, value]) => {
      result[key] = {
        ...value,
        default: undefined,
      };

      return result;
    },
    {}
  );
};

// Reusable helper function for boolean attributes
export const createBooleanAttribute = (name: string) => {
  return {
    default: undefined,
    parseHTML: (element: HTMLElement) => {
      const value = element.getAttribute(name);

      if (value === 'true') return true;
      if (value === 'false') return false;

      return;
    },
    renderHTML: (attrs: Record<string, any>) => {
      if (typeof attrs[name] === 'boolean') {
        const bool = `${attrs[name]}`;
        return { [name]: bool };
      }

      return;
    },
  } as Attribute;
};

// Reusable helper function for serialized attributes
export const createSerializedAttribute = (name: string) => {
  return {
    default: undefined,
    parseHTML: (element: HTMLElement) => {
      const value = element.getAttribute(name);

      if (value) {
        try {
          return JSON.parse(value);
        } catch (error) {
          console.error(`Error parsing JSON attribute ${name}:`, error);
          return;
        }
      }
      return;
    },
    renderHTML: (attrs: Record<string, any>) => {
      if (typeof attrs[name] === 'object') {
        return { [name]: JSON.stringify(attrs[name]) };
      }

      return;
    },
  } as Attribute;
};
